import { useRef, type KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'
import { tabId, type RecordTab } from './recordTabModel'

export type { RecordTab }

/**
 * The tabs of a relation page (ARIA tabs with a roving tab index): ←/→ wrap around, Home
 * and End jump; a modified key (Alt+← is the browser's "back") is left alone. Only the
 * tabs given exist, so the keyboard never lands on a tab hidden at this width.
 */
export function RecordTabs({
  label,
  tabs,
  active,
  onSelect,
  idPrefix,
}: {
  label: string
  tabs: readonly RecordTab[]
  active: string
  onSelect: (key: string) => void
  idPrefix: string
}) {
  const buttons = useRef(new Map<string, HTMLButtonElement>())

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
    const count = tabs.length
    const target =
      event.key === 'ArrowRight'
        ? (index + 1) % count
        : event.key === 'ArrowLeft'
          ? (index - 1 + count) % count
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? count - 1
              : null
    if (target === null) return
    event.preventDefault()
    const { key } = tabs[target]
    onSelect(key)
    buttons.current.get(key)?.focus()
  }

  return (
    <div role="tablist" aria-label={label} className="flex gap-1 overflow-x-auto rounded-xl bg-white/90 px-2 shadow-sm">
      {tabs.map((tab, index) => {
        const selected = tab.key === active
        return (
          <button
            key={tab.key}
            ref={(node) => {
              if (node) buttons.current.set(tab.key, node)
              else buttons.current.delete(tab.key)
            }}
            id={tabId(idPrefix, tab.key)}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={selected ? `${idPrefix}-panel` : undefined}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(tab.key)}
            onKeyDown={(event) => onKeyDown(event, index)}
            // The list scrolls sideways on a phone and so clips what sticks out of it: the focus
            // ring is drawn inside the tab, and there is no -mb-px to make the bar scroll upwards.
            className={cn(
              'shrink-0 border-b-2 px-3 py-2 text-sm font-medium focus-visible:-outline-offset-2 max-md:min-h-11',
              selected ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.count === undefined ? tab.label : `${tab.label} ${tab.count}`}
          </button>
        )
      })}
    </div>
  )
}
